import { useCallback } from 'react';
import classnames from 'classnames';
import { UnderlinedButton } from '@JohnLewisPartnership/jl-design-system/elements/button/Button';
import styles from './AlternativeSearchTerms.module.scss';
import StatefulLink from '../../stateful-link/StatefulLink';

import {
  dispatchAnalyticsEvent,
  ALSO_SEARCHED,
  GLOBAL_SEARCH_INTERACTION,
} from '../../analytics/analyticsEvents';
import { useAnalyticsData } from '../../../hooks/useAnalyticsData';
import { useAnalytics } from '../../../hooks/useAnalytics';
import { useAlternativeSuggestionData } from './data/useAlternativeSuggestionData';

type AlternateSuggestionProps = { suggestion: string };

type AlternativeSearchTermsProps = {
  alternativeSearchSuggestions: Array<{ searchTerm: string }>;
};

export const AlternateSuggestion = ({
  suggestion,
}: AlternateSuggestionProps) => {
  const { pageType } = useAnalyticsData();
  const { search } = useAlternativeSuggestionData();

  const alsoSearchedEvent = useAnalytics(ALSO_SEARCHED, {
    pagetype: pageType,
  });

  const handleClick = useCallback((searchTerm) => {
    search.setAlsoSearched(true);
    alsoSearchedEvent({ term: searchTerm });

    const altSearchData = {
      searchData: {
        type: 'similar search',
        term: search.term, // original typed term
        suggestion: searchTerm, // suggested term
      },
    };

    dispatchAnalyticsEvent(GLOBAL_SEARCH_INTERACTION, altSearchData);
  }, []);

  return (
    <StatefulLink
      className={classnames(styles['alternative-search-terms__link'])}
      query={{ 'search-term': suggestion }}
      facetId="" // facets might not be available for alt suggestions
      onClick={() => handleClick(suggestion)}
      data-testid="alternative-search-terms__suggestion__link"
    >
      <UnderlinedButton
        tag="span"
        className={classnames(
          styles['alternative-search-terms__suggestion'],
          styles['alternative-search-terms__suggestion--also-searched'],
          styles[
            'alternative-search-terms__suggestion--also-searched--enhanced'
          ]
        )}
        data-testid="alternative-search-terms__suggestion"
      >
        {suggestion}
      </UnderlinedButton>
    </StatefulLink>
  );
};

export const AlternativeSearchTerms = ({
  alternativeSearchSuggestions,
}: AlternativeSearchTermsProps) => {
  const MAX_SEARCH_SUGGESTIONS = 5;

  if (
    !alternativeSearchSuggestions ||
    !Array.isArray(alternativeSearchSuggestions) ||
    alternativeSearchSuggestions.length === 0
  )
    return null;

  return (
    <div
      data-testid="alternative-search-terms"
      className={classnames(
        styles['alternative-search-terms'],
        styles['alternative-search-terms--also-searched'],
        styles['alternative-search-terms--enhanced']
      )}
    >
      <p>
        {'Similar searches:'}
        <span
          className={classnames(
            styles['alternative-search-terms__suggestion--results'],
            styles['alternative-search-terms__suggestion--results--enhanced']
          )}
        >
          {alternativeSearchSuggestions.map((suggestion, i) => {
            if (i >= MAX_SEARCH_SUGGESTIONS) {
              return null;
            }
            return (
              <AlternateSuggestion
                suggestion={suggestion.searchTerm}
                key={suggestion.searchTerm}
              />
            );
          })}
        </span>
      </p>
    </div>
  );
};
